import { jsPDF } from "jspdf";

const COLORS = {
  brand: [74, 93, 78],
  text: [42, 51, 43],
  textMuted: [92, 102, 93],
  accent: [194, 122, 98],
  bg: [253, 251, 247],
  bg2: [243, 239, 234],
  border: [230, 226, 216],
  warn: [194, 122, 98],
};

export function exportRecommendationPDF(rec, { ownerName = "" } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;
  let y = M;

  const addPageIfNeeded = (needed) => {
    if (y + needed > H - M) {
      doc.addPage();
      y = M;
    }
  };

  // Header bar
  doc.setFillColor(...COLORS.bg2);
  doc.rect(0, 0, W, 100, "F");
  // Brand mark — small sage sprig
  doc.setDrawColor(...COLORS.brand);
  doc.setLineWidth(1.2);
  doc.line(M + 8, 56, M + 8, 36); // stem
  doc.setFillColor(...COLORS.brand);
  // Three leaves as small triangles
  doc.triangle(M + 8, 50, M + 18, 46, M + 18, 50, "F");
  doc.triangle(M + 8, 44, M + 0, 40, M + 0, 44, "F");
  doc.triangle(M + 8, 38, M + 18, 34, M + 18, 38, "F");
  // Accent dot
  doc.setFillColor(...COLORS.accent);
  doc.circle(M + 8, 32, 2.5, "F");

  doc.setTextColor(...COLORS.text);
  doc.setFontSize(10);
  doc.setFont("times", "normal");
  doc.setCharSpace(3);
  doc.text("a l t e r n a v i t", M + 32, 50);
  doc.setCharSpace(0);

  doc.setTextColor(...COLORS.textMuted);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("WELLNESS PLAN", M + 32, 66);
  y = 128;

  // Big title under header
  doc.setFont("times", "italic");
  doc.setFontSize(22);
  doc.setTextColor(...COLORS.text);
  doc.text("Your personalised supplement plan", M, y);
  y += 28;

  if (ownerName) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textMuted);
    doc.text(`Prepared for ${ownerName}  ·  ${new Date(rec.created_at).toLocaleDateString(undefined, { dateStyle: "long" })}`, M, y);
    y += 22;
  }

  // Profile block
  if (rec.profile) {
    doc.setFillColor(...COLORS.bg2);
    doc.roundedRect(M, y, W - M * 2, 84, 8, 8, "F");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textMuted);
    doc.setFont("helvetica", "bold");
    doc.text("PROFILE", M + 14, y + 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.text);
    const p = rec.profile;
    const cols = [
      `Age: ${p.age_range || "-"}`,
      `Gender: ${p.gender || "-"}`,
      `Activity: ${p.activity_level || "-"}`,
      `Diet: ${p.diet || "-"}`,
      `Sleep: ${p.sleep_quality || "-"}`,
      `Stress: ${p.stress_level || "-"}`,
    ];
    cols.forEach((c, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      doc.text(c, M + 14 + col * ((W - M * 2) / 2), y + 38 + row * 16);
    });
    y += 96;
  }

  // AI note
  if (rec.ai_explanation) {
    addPageIfNeeded(120);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.accent);
    doc.setFont("helvetica", "bold");
    doc.text("A NOTE FOR YOU", M, y);
    y += 16;
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    doc.setFont("times", "italic");
    const lines = doc.splitTextToSize(rec.ai_explanation, W - M * 2);
    lines.forEach((l) => {
      addPageIfNeeded(16);
      doc.text(l, M, y);
      y += 14;
    });
    y += 12;
  }

  // Supplements
  rec.supplements.forEach((s) => {
    addPageIfNeeded(160);
    doc.setDrawColor(...COLORS.border);
    doc.setFillColor(255, 255, 255);
    const startY = y;
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textMuted);
    doc.setFont("helvetica", "bold");
    doc.text((s.category || "").toUpperCase(), M, y + 4);
    y += 18;
    doc.setFontSize(16);
    doc.setFont("times", "bold");
    doc.setTextColor(...COLORS.text);
    doc.text(s.name, M, y);
    y += 18;

    if (s.addresses && s.addresses.length) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.textMuted);
      doc.text(`For: ${s.addresses.join(" · ")}`, M, y);
      y += 14;
    }

    doc.setFontSize(10);
    doc.setTextColor(...COLORS.text);
    const bLines = doc.splitTextToSize(s.benefits || "", W - M * 2);
    bLines.forEach((l) => {
      addPageIfNeeded(14);
      doc.text(l, M, y);
      y += 13;
    });

    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.brand);
    doc.text("DOSAGE", M, y);
    doc.text("TIMING", M + (W - M * 2) / 2, y);
    y += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.text);
    doc.text(s.dosage || "-", M, y);
    doc.text(s.timing || "-", M + (W - M * 2) / 2, y);
    y += 18;

    if (s.warnings || s.interactions) {
      doc.setFillColor(251, 244, 240);
      const blockH = 56;
      addPageIfNeeded(blockH);
      doc.roundedRect(M, y, W - M * 2, blockH, 6, 6, "F");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.warn);
      doc.setFont("helvetica", "bold");
      doc.text("WARNINGS / INTERACTIONS", M + 12, y + 16);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
      const w = doc.splitTextToSize(`${s.warnings || ""}  ${s.interactions || ""}`, W - M * 2 - 24);
      doc.text(w.slice(0, 3), M + 12, y + 30);
      y += blockH + 14;
    }

    // Separator
    doc.setDrawColor(...COLORS.border);
    doc.line(M, y, W - M, y);
    y += 18;
  });

  // Disclaimer
  addPageIfNeeded(80);
  y += 8;
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.textMuted);
  doc.setFont("helvetica", "italic");
  const disc = doc.splitTextToSize(
    "This document is educational only and is not a substitute for personalised medical advice. Please consult a qualified healthcare professional before starting any new supplement, especially if you take medications, are pregnant or nursing, or have a chronic condition.",
    W - M * 2
  );
  disc.forEach((l) => {
    addPageIfNeeded(12);
    doc.text(l, M, y);
    y += 11;
  });

  // Footer page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textMuted);
    doc.setFont("helvetica", "normal");
    doc.setCharSpace(2);
    doc.text(`alternavit  ·  page ${i} of ${pageCount}`, M, H - 18);
    doc.setCharSpace(0);
  }

  doc.save(`alternavit-plan-${new Date(rec.created_at).toISOString().slice(0, 10)}.pdf`);
}
